import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, LayoutGrid, LogOut, IdCard } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useState } from "react";
import LatestProductBanner from "./LatestProductBanner";

export default function Navbar() {
  const { customer, logout } = useApp();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  const cartCount = customer?.cart?.reduce((a, b) => a + Number(b.boxes || 0), 0) || 0;
  const displayName = customer?.person_name || customer?.name;
  const doLogout = () => { logout(); nav("/"); };

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#1A1A1A]/10"
      data-testid="navbar"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-brand">
          <img
            src="/tileroom-logo.png"
            alt="TileRoom — design your room here."
            className="h-24 md:h-28 w-auto object-contain"
          />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {customer && <Link to="/catalog" className="link-slide text-sm tracking-wide" data-testid="nav-catalog">Catalog</Link>}
          {customer && <Link to="/browse-by-size" className="link-slide text-sm tracking-wide" data-testid="nav-sizes">Sizewise</Link>}
          {customer && <Link to="/tile-layers" className="link-slide text-sm tracking-wide" data-testid="nav-layers">Tile Layers</Link>}
          <Link to="/reviews" className="link-slide text-sm tracking-wide" data-testid="nav-reviews">Reviews</Link>
          {customer && <Link to="/refer" className="link-slide text-sm tracking-wide" data-testid="nav-refer">Refer</Link>}
          {customer && <Link to="/projects" className="link-slide text-sm tracking-wide" data-testid="nav-projects">Projects</Link>}
          {customer && <Link to="/credits" className="link-slide text-sm tracking-wide" data-testid="nav-credits">Credits</Link>}
          {customer && <Link to="/returns" className="link-slide text-sm tracking-wide" data-testid="nav-returns">Returns</Link>}
          {customer && <Link to="/orders" className="link-slide text-sm tracking-wide" data-testid="nav-orders">Orders</Link>}
          {customer && <Link to="/yet-to-receive" className="link-slide text-sm tracking-wide" data-testid="nav-ytr">Yet to Receive</Link>}
          {!customer && (
            <Link to="/admin" className="link-slide text-xs tracking-[0.2em] uppercase text-[#8A8A8A]" data-testid="nav-admin">
              <LayoutGrid className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Admin
            </Link>
          )}
          {!customer && (
            <Link to="/employee/login" className="link-slide text-xs tracking-[0.2em] uppercase text-[#8A8A8A]" data-testid="nav-employee">
              <IdCard className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Employee
            </Link>
          )}
        </div>

        <div className="flex items-center gap-4">
          {customer && (
            <Link to="/cart" className="relative flex items-center gap-2 text-sm" data-testid="nav-cart">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-[#1E2C5A] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>
          )}
          {customer ? (
            <button
              className="hidden sm:inline-flex items-center gap-1 text-xs tracking-[0.15em] uppercase text-[#5C5C5C] hover:text-[#1E2C5A] transition-colors"
              onClick={doLogout}
              data-testid="nav-logout"
            >
              {(displayName || "").split(" ")[0]} · Sign Out <LogOut className="w-3 h-3" strokeWidth={1.5} />
            </button>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline text-xs tracking-[0.2em] uppercase text-[#5C5C5C] hover:text-[#1E2C5A] transition-colors" data-testid="nav-signin">Sign In</Link>
              <Link to="/signup" className="hidden sm:inline btn-outline-ink !px-5 !py-2 text-xs" data-testid="nav-signup">Sign Up</Link>
            </>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#1A1A1A]/10 bg-[#FAF9F6] px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          <Link to="/catalog" onClick={() => setOpen(false)} className="text-sm">Catalog</Link>
          {customer && <Link to="/browse-by-size" onClick={() => setOpen(false)} className="text-sm">Sizewise</Link>}
          <Link to="/tile-layers" onClick={() => setOpen(false)} className="text-sm">Tile Layers</Link>
          <Link to="/reviews" onClick={() => setOpen(false)} className="text-sm">Reviews</Link>
          {customer && <Link to="/refer" onClick={() => setOpen(false)} className="text-sm">Refer</Link>}
          {customer && <Link to="/orders" onClick={() => setOpen(false)} className="text-sm">Orders</Link>}
          {customer && <Link to="/yet-to-receive" onClick={() => setOpen(false)} className="text-sm">Yet to Receive</Link>}
          {!customer && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm text-[#8A8A8A]">Admin</Link>}
          {!customer && <Link to="/employee/login" onClick={() => setOpen(false)} className="text-sm text-[#8A8A8A]">Employee Portal</Link>}
          {!customer ? (
            <div className="flex gap-3 pt-2 border-t border-[#1A1A1A]/10">
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm">Sign In</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="text-sm text-[#1E2C5A]">Sign Up</Link>
            </div>
          ) : (
            <button onClick={() => { doLogout(); setOpen(false); }} className="text-sm text-left">Sign Out</button>
          )}
        </div>
      )}
      {customer && <LatestProductBanner />}
    </nav>
  );
}
